import { cn } from '#client/lib/cn';

interface ChartSkeletonProps {
  height?: number | undefined;
  /** `bars` for a bar chart, `line` for a line, `ring` for a donut. */
  shape?: 'line' | 'bars' | 'ring' | undefined;
  className?: string | undefined;
}

/**
 * A placeholder with the chart's geometry, not a spinner.
 *
 * Mirroring the real layout is what stops the page reflowing when the data
 * lands — the same reason `DashboardSkeleton` mirrors the dashboard rather than
 * showing one large box. A chart that appears and shoves the table below it
 * down the page costs more attention than the wait did.
 *
 * `aria-hidden`, like every other skeleton here: the loading state is announced
 * once by the region that owns it, not by each shape inside it.
 */
export function ChartSkeleton({ height = 240, shape = 'line', className }: ChartSkeletonProps) {
  return (
    <div
      className={cn('c-chart-skeleton', `c-chart-skeleton--${shape}`, className)}
      style={{ blockSize: `${height}px` }}
      aria-hidden="true"
    >
      {shape === 'bars' &&
        // Varied heights, fixed rather than random: a skeleton that reshuffles
        // on every render draws the eye to itself.
        [62, 88, 45, 74, 96, 58, 81].map((percent, index) => (
          <span
            key={index}
            className="c-chart-skeleton__bar"
            style={{ blockSize: `${percent}%` }}
          />
        ))}
      {shape === 'line' && <span className="c-chart-skeleton__line" />}
      {shape === 'ring' && <span className="c-chart-skeleton__ring" />}
    </div>
  );
}
